import React from 'react';
import { Search, Compass, Layers, Rocket } from 'lucide-react';

const ProcessSection: React.FC = () => {
  const steps = [
    { 
      num: '01', 
      icon: <Search className="w-6 h-6 text-[#D4AF37] mb-6" />,
      title: 'Assess', 
      desc: 'Investor readiness audit, credibility gaps, and perception analysis to identify your baseline positioning.' 
    },
    { 
      num: '02', 
      icon: <Compass className="w-6 h-6 text-[#D4AF37] mb-6" />,
      title: 'Position', 
      desc: 'Defining your unique commercial story, ESG narrative, and competitive advantage for global markets.' 
    },
    { 
      num: '03', 
      icon: <Layers className="w-6 h-6 text-[#D4AF37] mb-6" />,
      title: 'Package', 
      desc: 'Building institutional-grade investor documents, buyer materials, and a high-trust digital presence.' 
    },
    { 
      num: '04', 
      icon: <Rocket className="w-6 h-6 text-[#D4AF37] mb-6" />,
      title: 'Activate', 
      desc: 'Deploying strategic visibility systems and supporting market entry to connect with capital and buyers.' 
    }
  ];

  return (
    <section id="process" className="py-32 max-w-[1440px] mx-auto px-8 scroll-mt-32">
      <div className="flex flex-col gap-16">
        {/* Narrative Header - Level 1 label */}
        <div className="max-w-4xl">
          <span className="text-zinc-400 text-[11px] font-black uppercase tracking-[0.3em] mb-6 block">Tactical Roadmap</span>
          <h2 className="text-3xl md:text-5xl font-bold tracking-tighter leading-tight text-zinc-950 mb-8">
            Our Mine-to-Market Framework.
          </h2>
          <p className="text-zinc-600 text-lg leading-relaxed">
            We don’t improvise. We run a proven commercial positioning system designed to ensure your mine looks as serious as it actually is, bridging the gap between operational reality and institutional perception.
          </p>
        </div>

        {/* Grid of Steps - Glass Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {steps.map((step, i) => (
            <div 
              key={i} 
              className="p-10 bg-white/60 backdrop-blur-sm shadow-sm hover:shadow-2xl hover:bg-white transition-all group relative overflow-hidden rounded-sm border border-white/60"
            >
              <div className="absolute top-4 right-4 text-[11px] font-black text-zinc-200 group-hover:text-[#D4AF37]/20 transition-colors">
                {step.num}
              </div>
              <div className="p-3 bg-white rounded-sm inline-block mb-2 transition-all shadow-none">
                {step.icon}
              </div>
              <h4 className="font-bold text-[14px] uppercase tracking-widest mb-3 text-zinc-950">{step.title}</h4>
              <p className="text-sm text-zinc-500 leading-relaxed max-w-md">{step.desc}</p>
            </div>
          ))}
        </div>

        {/* Glass Audit Callout */}
        <div className="mt-8 p-8 md:p-12 bg-zinc-950/90 backdrop-blur-xl text-white rounded-sm flex flex-col md:flex-row justify-between items-center gap-10 shadow-[0_30px_60px_rgba(0,0,0,0.4)] border border-white/10 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-[#D4AF37]/5 to-transparent pointer-events-none" />
          <div className="max-w-xl text-center md:text-left relative z-10">
            <h3 className="text-2xl md:text-3xl font-bold tracking-tighter mb-4">Not Sure Where Your Mine Stands?</h3>
            <p className="text-zinc-300 text-lg leading-relaxed opacity-90">
              Obtain a comprehensive credibility scorecard and a clear strategic roadmap through our Mine Visibility & Investor Readiness Audit.
            </p>
          </div>
          <a 
            href="https://wa.me/27839287731"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-[linear-gradient(to_right,#D4AF37_0%,#F7E08A_20%,#FFF9E3_40%,#FFFFFF_50%,#FFF9E3_60%,#F7E08A_80%,#D4AF37_100%)] animate-gold-shimmer text-zinc-950 px-6 md:px-10 py-4 md:py-5 rounded-sm font-black text-[11px] md:text-[13px] uppercase tracking-[0.1em] md:tracking-[0.2em] hover:brightness-110 transition-all whitespace-nowrap border-none shadow-lg relative z-10"
          >
            Request Audit
          </a>
        </div>
      </div>
    </section>
  );
};

export default ProcessSection;